import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Save, Users, Calendar } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

export default function MentorRosterPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [mentors, setMentors] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rosterData, setRosterData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [year, month]); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    try {
      const [mentorsRes, shiftsRes, rosterRes] = await Promise.all([
        api.get('/users?roles=mentor,academic_master,master_of_academics,master_of_academics_').catch(() => ({ data: [] })),
        api.get('/hr/shifts'),
        api.get(`/hr/mentor-roster?year=${year}&month=${month + 1}`),
      ]);
      
      const mentorList = (mentorsRes.data || []).filter(u => u.is_active !== false);
      setMentors(mentorList);
      setShifts(shiftsRes.data || []);
      
      // Build roster map: mentor_id -> { shift_id, daily_shifts: { "1": shiftId, "2": shiftId, ... } }
      const map = {};
      (rosterRes.data || []).forEach(r => {
        map[r.mentor_id] = {
          shift_id: r.shift_id,
          daily_shifts: r.daily_shifts || {},
        };
      });
      setRosterData(map);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  function setDailyShift(mentorId, day, shiftId) {
    setRosterData(prev => {
      const existing = prev[mentorId] || { shift_id: null, daily_shifts: {} };
      return {
        ...prev,
        [mentorId]: {
          ...existing,
          daily_shifts: { ...existing.daily_shifts, [String(day)]: shiftId },
        },
      };
    });
  }

  function setDefaultShift(mentorId, shiftId) {
    setRosterData(prev => {
      const existing = prev[mentorId] || { shift_id: null, daily_shifts: {} };
      // Fill all working days with this shift
      const daysInMonth = getDaysInMonth(year, month);
      const dailyShifts = { ...existing.daily_shifts };
      for (let d = 1; d <= daysInMonth; d++) {
        const dow = new Date(year, month, d).getDay();
        if (dow !== 0 && dow !== 6) { // Skip weekends
          dailyShifts[String(d)] = shiftId;
        }
      }
      return {
        ...prev,
        [mentorId]: { shift_id: shiftId, daily_shifts: dailyShifts },
      };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const entries = mentors.map(m => ({
        mentor_id: m.id,
        mentor_name: m.full_name,
        shift_id: rosterData[m.id]?.shift_id || null,
        daily_shifts: rosterData[m.id]?.daily_shifts || {},
      }));
      await api.post('/hr/mentor-roster', { year, month: month + 1, entries });
      toast.success('Roster saved successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save roster');
    } finally {
      setSaving(false);
    }
  }

  const daysInMonth = getDaysInMonth(year, month);
  const dayHeaders = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, month, d).getDay();
    dayHeaders.push({ day: d, dow, isWeekend: dow === 0 || dow === 6 });
  }

  const shiftMap = useMemo(() => {
    const m = {};
    shifts.forEach(s => { m[s.id] = s; });
    return m;
  }, [shifts]);

  function getShiftBadge(shiftId) {
    if (!shiftId) return null;
    const s = shiftMap[shiftId];
    if (!s) return <Badge variant="outline" className="text-[9px] px-1">?</Badge>;
    const colors = {
      'Morning': 'bg-blue-500/20 text-blue-600',
      'Afternoon': 'bg-orange-500/20 text-orange-600',
      'India': 'bg-green-500/20 text-green-600',
    };
    const color = Object.entries(colors).find(([k]) => s.name.includes(k))?.[1] || 'bg-muted text-foreground';
    return <Badge className={`${color} text-[9px] px-1 py-0`}>{s.name.split(' ')[0]}</Badge>;
  }

  return (
    <div className="space-y-6" data-testid="mentor-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Mentor Roster</h1>
          <p className="text-muted-foreground text-sm mt-1">Monthly shift roster for mentors and academics team</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-roster-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Roster'}
        </Button>
      </div>

      {/* Month Navigation */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="roster-prev-month">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-lg font-bold">{MONTHS[month]} {year}</h2>
              <p className="text-xs text-muted-foreground">{mentors.length} mentors | {shifts.length} shifts defined</p>
            </div>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="roster-next-month">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : mentors.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No Mentors Found</h3>
            <p className="text-muted-foreground text-sm">No active mentors or academics staff found.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card z-10 min-w-[180px]">Mentor</TableHead>
                  <TableHead className="min-w-[140px]">Default Shift</TableHead>
                  {dayHeaders.map(dh => (
                    <TableHead
                      key={dh.day}
                      className={`text-center min-w-[60px] text-xs ${dh.isWeekend ? 'bg-muted/30' : ''}`}
                    >
                      <div>{dh.day}</div>
                      <div className="text-[10px] text-muted-foreground">{['S','M','T','W','T','F','S'][dh.dow]}</div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mentors.map((mentor, mi) => {
                  const roster = rosterData[mentor.id] || {};
                  return (
                    <TableRow key={mentor.id} data-testid={`roster-row-${mi}`}>
                      <TableCell className="sticky left-0 bg-card z-10 font-medium text-sm">
                        {mentor.full_name}
                        {mentor.role && (
                          <span className="text-[10px] text-muted-foreground block">
                            {mentor.role.replace(/_/g, ' ')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={roster.shift_id || ''}
                          onValueChange={v => setDefaultShift(mentor.id, v)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`default-shift-${mi}`}>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {shifts.map(s => (
                              <SelectItem key={s.id} value={s.id} className="text-xs">
                                {s.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      {dayHeaders.map(dh => {
                        const dailyShift = roster.daily_shifts?.[String(dh.day)];
                        return (
                          <TableCell
                            key={dh.day}
                            className={`text-center p-1 ${dh.isWeekend ? 'bg-muted/20' : ''}`}
                          >
                            {dh.isWeekend ? (
                              <span className="text-[10px] text-muted-foreground">Off</span>
                            ) : (
                              <Select
                                value={dailyShift || ''}
                                onValueChange={v => setDailyShift(mentor.id, dh.day, v)}
                              >
                                <SelectTrigger className="h-7 w-full text-[10px] px-1 border-dashed">
                                  <SelectValue>
                                    {dailyShift ? getShiftBadge(dailyShift) : <span className="text-muted-foreground">-</span>}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {shifts.map(s => (
                                    <SelectItem key={s.id} value={s.id} className="text-xs">
                                      {s.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Shift Legend */}
      {shifts.length > 0 && (
        <div className="flex gap-4 flex-wrap">
          {shifts.map(s => (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              {getShiftBadge(s.id)}
              <span className="text-muted-foreground">{s.start} - {s.end}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
